import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";

const KEY = ["admin", "products"] as const;

export function useProducts(scope?: string) {
  return useQuery({
    queryKey: [...KEY, { scope }],
    queryFn: () => api.listProducts(true, scope),
  });
}

export function useCreateProduct(scope?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: api.CreateProductPayload) => api.createProduct(p, scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateProduct(scope?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; payload: api.UpdateProductPayload }) =>
      api.updateProduct(v.id, v.payload, scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteProduct(scope?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteProduct(id, scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

const RULES_KEY = ["admin", "rules"] as const;

export function useRules(productId: number | null, scope?: string) {
  return useQuery({
    queryKey: [...RULES_KEY, productId, { scope }],
    queryFn: () => api.listRules(productId!, scope),
    enabled: productId !== null,
  });
}

export function useReplaceRules(scope?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { productId: number; rules: api.Rule[] }) =>
      api.replaceRules(v.productId, v.rules, scope),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: [...RULES_KEY, v.productId] }),
  });
}

const MODS_KEY = ["admin", "modifiers"] as const;

export function useModifiers(productId: number | null, scope?: string) {
  return useQuery({
    queryKey: [...MODS_KEY, productId, { scope }],
    queryFn: () => api.listModifiers(productId!, scope),
    enabled: productId !== null,
  });
}

export function useReplaceModifiers(scope?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { productId: number; modifiers: api.Modifier[] }) =>
      api.replaceModifiers(v.productId, v.modifiers, scope),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: [...MODS_KEY, v.productId] }),
  });
}

export function useCloneGlobal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (force: boolean) => api.cloneGlobal(force),
    // 产品、规则、修饰项、提示词、LLM 配置都可能变化
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
}
